// Every page, by the URL it answers. A feature's pages load with its first route, from the feature's index.
import type { ComponentType } from "react";
import type { RouteObject } from "react-router";
import { Navigate, useParams } from "react-router";
import { LoginPage } from "@/features/auth";
import { ProjectLayout } from "@/features/projects/ProjectLayout";
import { ProjectProvider } from "@/platform/project-context";
import { RouteError } from "./RouteError";
import { Shell } from "./Shell";

function page<M>(load: () => Promise<M>, pick: (m: M) => ComponentType) {
  return async () => ({ Component: pick(await load()) });
}

/** Old group links open the Datasets page filtered to that group. */
function GroupRedirect() {
  const { groupId = "" } = useParams();
  return <Navigate replace to={`../datasets?group=${encodeURIComponent(groupId)}`} />;
}

const account = () => import("@/features/account");
const activity = () => import("@/features/activity");
const admin = () => import("@/features/admin");
const monitoring = () => import("@/features/monitoring");
const modelsWorkspace = () => import("./ModelsWorkspace");
const datasets = () => import("@/features/datasets");
const jobs = () => import("@/features/jobs");
const lineage = () => import("@/features/lineage");
const workflows = () => import("@/features/workflows");
const changes = () => import("@/features/changes");
const models = () => import("@/features/models");
const inference = () => import("@/features/models/inference");
const trainingRecords = () => import("@/features/training-records");
const projects = () => import("@/features/projects");
const receipts = () => import("@/features/receipts");
const retire = () => import("@/features/retire");
const subsets = () => import("@/features/subsets");
const reviews = () => import("@/features/reviews");
const uploads = () => import("@/features/uploads");
const views = () => import("@/features/views");

export const routes: RouteObject[] = [
  ...(import.meta.env.DEV ? [{ path: "/__preview/inference", lazy: page(() => import("@/preview/inference/InferencePreviewPage"), (m) => m.InferencePreviewPage) }] : []),
  ...(import.meta.env.DEV ? [{ path: "/__preview/data", lazy: page(() => import("@/preview/DataPreviewPage"), (m) => m.DataPreviewPage) }] : []),
  { path: "/login", element: <LoginPage /> },
  {
    element: <Shell />,
    // The shell's own failure is shown full page; a page that fails to load or render keeps the navigation.
    errorElement: <RouteError />,
    children: [{
      errorElement: <RouteError />,
      children: [
        { index: true, lazy: page(projects, (m) => m.HomePage) },
        { path: "projects", lazy: page(projects, (m) => m.ProjectsPage) },
        { path: "inbox", lazy: page(activity, (m) => m.InboxPage) },
        { path: "settings", lazy: page(account, (m) => m.AccountPage) },
        { path: "docs/cli/:guide?", lazy: page(() => import("@/features/documentation"), (m) => m.CliDocumentationPage) },
        { path: "base-models", lazy: page(modelsWorkspace, (m) => m.LegacyModelFactoryRedirect) },
        { path: "model-factory", lazy: page(modelsWorkspace, (m) => m.LegacyModelFactoryRedirect) },
        { path: "admin", lazy: page(admin, (m) => m.AdminPage) },
        { path: "admin/monitoring/:kind/:id", lazy: page(monitoring, (m) => m.MonitoringEntityPage) },
        { path: "admin/*", element: <Navigate replace to="/admin" /> },
        { path: "jobs/:id", lazy: page(jobs, (m) => m.JobPage) },
        { path: "datasets/:id/v/:n", lazy: page(datasets, (m) => m.DatasetLink) },
        { path: "models", lazy: page(modelsWorkspace, (m) => m.ModelsWorkspace) },
        { path: "models/:id", lazy: page(models, (m) => m.ModelLink) },
        {
          path: "p/:slug",
          element: <ProjectProvider><ProjectLayout /></ProjectProvider>,
          children: [
            { index: true, lazy: page(projects, (m) => m.OverviewPage) },
            { path: "datasets", lazy: page(datasets, (m) => m.DatasetsPage) },
            { path: "datasets/:name", lazy: page(datasets, (m) => m.DatasetPage) },
            { path: "datasets/:name/compare", lazy: page(datasets, (m) => m.ComparePage) },
            { path: "datasets/:name/v/:ref/analysis", lazy: page(datasets, (m) => m.DatasetAnalysisPage) },
            { path: "datasets/:name/v/:ref", lazy: page(datasets, (m) => m.VersionPage) },
            { path: "datasets/:name/v/:ref/samples/:sha", lazy: page(lineage, (m) => m.SampleHistoryPage) },
            { path: "datasets/:name/drafts/:number", lazy: page(datasets, (m) => m.DraftPage) },
            { path: "datasets/:name/subsets/new", lazy: page(subsets, (m) => m.SubsetBuilderPage) },
            { path: "merge-plans/new", lazy: page(changes, (m) => m.MergeSourcePickerPage) },
            { path: "merge-plans", lazy: page(changes, (m) => m.MergePlansPage) },
            { path: "merge-plans/:number", lazy: page(changes, (m) => m.MergePlanPage) },
            { path: "merge-plans/:number/conflicts", lazy: page(changes, (m) => m.PlanConflictsPage) },
            { path: "label-exports/:number", lazy: page(changes, (m) => m.LabelExportPage) },
            { path: "groups", element: <Navigate replace to="../datasets" /> },
            { path: "groups/:groupId", element: <GroupRedirect /> },
            { path: "uploads", lazy: page(uploads, (m) => m.UploadsPage) },
            { path: "uploads/new", lazy: page(uploads, (m) => m.NewUploadPage) },
            { path: "upload-sessions/:sessionId", lazy: page(uploads, (m) => m.UploadSessionPage) },
            { path: "views", lazy: page(views, (m) => m.ViewsPage) },
            { path: "subsets", lazy: page(subsets, (m) => m.SubsetsPage) },
            { path: "views/new", lazy: page(views, (m) => m.NewViewPage) },
            { path: "views/:ref/analysis", lazy: page(() => import("@/app/data-analysis/ViewEdaPage"), (m) => m.ViewEdaPage) },
            { path: "views/:ref", lazy: page(views, (m) => m.ViewPage) },
            { path: "models", lazy: page(modelsWorkspace, (m) => m.ModelsWorkspace) },
            { path: "model-return", lazy: page(models, (m) => m.ReturnModelPage) },
            { path: "training-records/:kind/:id", lazy: page(trainingRecords, (m) => m.TrainingRecordPage) },
            { path: "inference", lazy: page(inference, (m) => m.InferenceWorkspace) },
            { path: "train", lazy: page(models, (m) => m.TrainSetupPage) },
            { path: "model-comparison", lazy: page(models, (m) => m.ModelComparePage) },
            { path: "model-evaluations/:id/analysis", lazy: page(models, (m) => m.AnalysisPage) },
            { path: "model-evaluations/:id", lazy: page(models, (m) => m.ModelEvaluationLink) },
            { path: "model-factory", lazy: page(modelsWorkspace, (m) => m.LegacyModelFactoryRedirect) },
            { path: "base-models", lazy: page(modelsWorkspace, (m) => m.LegacyModelFactoryRedirect) },
            { path: "models/submissions/:id", lazy: page(models, (m) => m.SubmissionPage) },
            { path: "models/registrations/:id", lazy: page(models, (m) => m.RegistrationPage) },
            { path: "models/:name", lazy: page(modelsWorkspace, (m) => m.ModelsWorkspace) },
            { path: "models/:name/compare", lazy: page(models, (m) => m.ModelComparePage) },
            { path: "models/:name/train", lazy: page(models, (m) => m.TrainSetupPage) },
            { path: "models/:name/:ref", lazy: page(models, (m) => m.VersionPage) },
            { path: "lineage", lazy: page(lineage, (m) => m.LineagePage) },
            { path: "workflows", lazy: page(workflows, (m) => m.WorkflowPage) },
            { path: "lineage/graph", lazy: page(lineage, (m) => m.GraphRedirect) },
            { path: "lineage/receipts/:number", lazy: page(lineage, (m) => m.ContributionPage) },
            { path: "reviews", lazy: page(uploads, (m) => m.ReviewsPage) },
            { path: "reviews/drafts/:dataset/:number/:revision", lazy: page(reviews, (m) => m.DraftReviewPage) },
            { path: "receipts", lazy: page(receipts, (m) => m.ReceiptsPage) },
            { path: "receipts/:number", lazy: page(receipts, (m) => m.ReceiptPage) },
            { path: "audit", lazy: page(activity, (m) => m.DataAuditPage) },
            { path: "jobs", lazy: page(jobs, (m) => m.JobsPage) },
            { path: "deletions", lazy: page(retire, (m) => m.DeletionsPage) },
            { path: "settings", lazy: page(projects, (m) => m.ProjectSettingsPage) },
          ],
        },
      ],
    }],
  },
];
